import { Link, useNavigate } from "react-router-dom";
import { Clock, Sparkle, Warning, CheckCircle, Tag, ArrowRight, Info } from "@phosphor-icons/react";
import { formatGBPDecimal, categoryLabel, scoreColor, daysUntil } from "../lib/api";
import { publicPrizeCell } from "../lib/displayValue";
import { totalTicketCapInfo } from "../lib/budgetOdds";
import { isBotbRecord, isBotbSkillBased } from "../lib/botbDisplay";
import PrizeImage from "./PrizeImage";
import AffiliateLinkLabel from "./AffiliateLinkLabel";
import { affiliateDisclosureFor } from "../lib/affiliateDisclosure";
import {
  currentTicketPrice,
  hasFreeOrPostalEntryMethod,
  publicOddsState,
} from "../lib/publicCompetitionContract";
import { createCatalogueDetailNavigationState } from "../lib/catalogueDetailNavigation";

const Badge = ({ children, variant = "neutral", icon: Icon, testId }) => {
  const styles = {
    gold: "bg-[var(--cc-gold)] text-[var(--cc-navy)]",
    good: "bg-emerald-100 text-emerald-800 border border-emerald-200",
    warn: "bg-amber-100 text-amber-800 border border-amber-200",
    bad: "bg-red-100 text-red-800 border border-red-200",
    neutral: "bg-slate-100 text-slate-700 border border-slate-200",
    dark: "bg-[var(--cc-navy)] text-white",
  }[variant];
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide ${styles}`}
    >
      {Icon && <Icon size={12} weight="fill" />}
      {children}
    </span>
  );
};

export default function CompetitionCard({ comp, index = 0, snapshotId = null }) {
  const navigate = useNavigate();
  const s = comp.scores;
  const operatorName = (comp.operator?.name || "").trim();
  const overall = s.overall_compcheck_score;
  const dleft = daysUntil(comp.draw_date);
  const isClosingSoon = dleft != null && dleft <= 3 && dleft >= 0;
  const isBestValue = s.value_score >= 80;
  const isTransparent = s.is_transparent === true;
  const dataQuality = s.data_quality_label; // 'transparent' | 'partial_data' | 'check_terms'
  const hasRisk = (s.risk_flags || []).length > 0;
  const capInfo = totalTicketCapInfo(comp);
  const hasTrustedTotalCap = Boolean(capInfo.totalTicketCap);
  const displayTicketPrice = currentTicketPrice(comp);
  const oddsState = publicOddsState(comp, capInfo);
  const cardOddsReady = oddsState.isReady;
  const isBotb = isBotbRecord(comp);
  const isBotbSkill = isBotbSkillBased(comp);
  const isMckinney = (
    String(comp.operator_key || "").toLowerCase() === "mckinney"
    || operatorName.toLowerCase() === "mckinney competitions"
  );
  const isMckinneyAwarenessOnly = isMckinney && (
    comp.mckinney_awareness_only === true
    || comp.mckinney_standard_odds_supported === false
  );
  const isLlf = (
    String(comp.operator_key || "").toLowerCase() === "llf"
    || operatorName.toLowerCase() === "llf games"
  );
  const isLlfAwarenessOnly = isLlf && (
    comp.llf_awareness_only === true
    || comp.llf_standard_odds_supported === false
  );
  const isKilted = (
    String(comp.operator_key || "").toLowerCase() === "kilted"
    || operatorName.toLowerCase() === "kilted competitions"
  );
  const isKiltedAwarenessOnly = isKilted && (
    comp.kilted_awareness_only === true
    || comp.kilted_standard_odds_supported === false
  );
  const isUkcc = (
    String(comp.operator_key || "").toLowerCase() === "ukcc"
    || operatorName.toLowerCase() === "ukcc competitions"
    || operatorName.toLowerCase() === "uk carp competitions"
  );
  const isUkccAwarenessOnly = isUkcc && (
    comp.ukcc_awareness_only === true
    || comp.ukcc_standard_odds_supported === false
  );
  const isBounty = (
    String(comp.operator_key || "").toLowerCase() === "bounty"
    || operatorName.toLowerCase() === "bounty competitions"
  );
  const isBountyAwarenessOnly = isBounty && (
    comp.bounty_awareness_only === true
    || comp.bounty_standard_odds_supported === false
  );
  const isStorm = (
    String(comp.operator_key || "").toLowerCase() === "storm"
    || operatorName.toLowerCase() === "storm competitions"
  );
  const isStormAwarenessOnly = isStorm && (
    comp.storm_awareness_only === true
    || comp.storm_standard_odds_supported === false
  );
  const isRedline = (
    String(comp.operator_key || "").toLowerCase() === "redline"
    || operatorName.toLowerCase() === "redline competitions"
  );
  const isRedlineAwarenessOnly = isRedline && (
    comp.redline_awareness_only === true
    || comp.redline_standard_odds_supported === false
  );
  const isPrizeParadise = (
    String(comp.operator_key || "").toLowerCase() === "prizeparadise"
    || operatorName.toLowerCase() === "prize paradise"
  );
  const isPrizeParadiseAwarenessOnly = isPrizeParadise && (
    comp.prizeparadise_awareness_only === true
    || comp.prizeparadise_standard_odds_supported === false
  );
  const isCaddy = (
    String(comp.operator_key || "").toLowerCase() === "caddy"
    || ["caddy comps", "caddy competitions"].includes(operatorName.toLowerCase())
  );
  const isCaddyAwarenessOnly = isCaddy && (
    comp.caddy_awareness_only === true
    || comp.caddy_standard_odds_supported === false
  );
  const isHotComps = (
    String(comp.operator_key || "").toLowerCase() === "hotcomps"
    || ["hot comps", "hot comps competitions"].includes(operatorName.toLowerCase())
  );
  const isHotCompsAwarenessOnly = isHotComps && (
    comp.hotcomps_awareness_only === true
    || comp.hotcomps_standard_odds_supported === false
  );
  const isApex = (
    String(comp.operator_key || "").toLowerCase() === "apex"
    || operatorName.toLowerCase() === "apex competitions"
  );
  const isApexAwarenessOnly = isApex && (
    comp.apex_awareness_only === true
    || comp.apex_standard_odds_supported === false
  );
  const isJammy = (
    String(comp.operator_key || "").toLowerCase() === "jammy"
    || ["jammy", "jammy competitions"].includes(operatorName.toLowerCase())
  );
  const isJammyAwarenessOnly = isJammy && (
    comp.jammy_awareness_only === true
    || comp.jammy_standard_odds_supported === false
  );
  const isPawsome = (
    String(comp.operator_key || "").toLowerCase() === "pawsome"
    || operatorName.toLowerCase() === "pawsome competitions"
  );
  const isPawsomeAwarenessOnly = isPawsome && (
    comp.pawsome_awareness_only === true
    || comp.pawsome_standard_odds_supported === false
  );
  const isLincolnshire = (
    String(comp.operator_key || "").toLowerCase() === "lincolnshire"
    || operatorName.toLowerCase() === "lincolnshire competitions"
  );
  const isLincolnshireAwarenessOnly = isLincolnshire && (
    comp.lincolnshire_awareness_only === true
    || comp.lincolnshire_standard_odds_supported === false
  );
  const isDrift = (
    String(comp.operator_key || "").toLowerCase() === "drift"
    || operatorName.toLowerCase() === "drift competitions"
  );
  const isDriftAwarenessOnly = isDrift && (
    comp.drift_awareness_only === true
    || comp.drift_standard_odds_supported === false
  );
  const isPalladium = (
    String(comp.operator_key || "").toLowerCase() === "palladium"
    || operatorName.toLowerCase() === "palladium competitions"
  );
  const isPalladiumAwarenessOnly = isPalladium && (
    comp.palladium_awareness_only === true
    || comp.palladium_standard_odds_supported === false
  );
  const isPristine = (
    String(comp.operator_key || "").toLowerCase() === "pristine"
    || operatorName.toLowerCase() === "pristine competitions"
  );
  const isPristineAwarenessOnly = isPristine && (
    comp.pristine_awareness_only === true
    || comp.pristine_standard_odds_supported === false
  );
  const isThatPrizeGuy = (
    String(comp.operator_key || "").toLowerCase() === "thatprizeguy"
    || operatorName.toLowerCase() === "that prize guy"
  );
  const isThatPrizeGuyAwarenessOnly = isThatPrizeGuy && (
    comp.thatprizeguy_awareness_only === true
    || comp.thatprizeguy_standard_odds_supported === false
  );
  const isBright = (
    String(comp.operator_key || "").toLowerCase() === "bright"
    || operatorName.toLowerCase() === "bright competitions"
  );
  const isBrightAwarenessOnly = isBright && (
    comp.bright_awareness_only === true
    || comp.bright_standard_odds_supported === false
  );
  const isSalexmo = (
    String(comp.operator_key || "").toLowerCase() === "salexmo"
    || operatorName.toLowerCase() === "salexmo"
  );
  const isSalexmoAwarenessOnly = isSalexmo && (
    comp.salexmo_awareness_only === true
    || comp.salexmo_standard_odds_supported === false
  );
  const isClick = (
    String(comp.operator_key || "").toLowerCase() === "click"
    || operatorName.toLowerCase() === "click competitions"
  );
  const isClickAwarenessOnly = isClick && comp.click_awareness_only === true;
  const isAwarenessOnly = (
    isMckinneyAwarenessOnly
    || isLlfAwarenessOnly
    || isKiltedAwarenessOnly
    || isUkccAwarenessOnly
    || isBountyAwarenessOnly
    || isStormAwarenessOnly
    || isRedlineAwarenessOnly
    || isPrizeParadiseAwarenessOnly
    || isCaddyAwarenessOnly
    || isHotCompsAwarenessOnly
    || isApexAwarenessOnly
    || isJammyAwarenessOnly
    || isPawsomeAwarenessOnly
    || isLincolnshireAwarenessOnly
    || isDriftAwarenessOnly
    || isThatPrizeGuyAwarenessOnly
    || isBrightAwarenessOnly
    || isSalexmoAwarenessOnly
    || isClickAwarenessOnly
  );
  const oddsUnavailableReason = oddsState.reason;
  // P0 integrity badges
  const dataStatus = comp.competition_data_status;
  const isDemoListing =
    comp.is_demo_seed || dataStatus === "seed_demo" || dataStatus === "source_pending_review";
  const ctaLabel = comp.cta_label || (comp.cta_target === "operator" ? "View operator site" : "View competition");
  const ctaDestinationUrl = comp.cta_destination_url || comp.outbound_url || comp.affiliate_url || comp.source_competition_url || comp.competition_url;
  const affiliateDisclosure = affiliateDisclosureFor(comp, ctaDestinationUrl);
  const isExternalCta =
    (comp.cta_target === "operator" || comp.cta_target === "affiliate" || comp.cta_target === "partner") &&
    Boolean(ctaDestinationUrl);
  const detailPath = `/competition/${comp.slug}`;
  const navigateToDetail = () => {
    const state = createCatalogueDetailNavigationState({
      snapshotId,
      slug: comp.slug,
    });
    if (state) {
      navigate(detailPath, { state });
      return;
    }
    navigate(detailPath);
  };
  const handleCardNavigate = (event) => {
    if (event.defaultPrevented) return;
    const target = event.target;
    if (target?.closest?.("a,button,input,select,textarea,[role='button']")) {
      return;
    }
    navigateToDetail();
  };
  const handleDetailLinkNavigate = (event) => {
    event.stopPropagation();
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.altKey
      || event.ctrlKey
      || event.shiftKey
    ) return;
    event.preventDefault();
    navigateToDetail();
  };

  return (
    <article
      onClick={handleCardNavigate}
      data-testid={`competition-card-${comp.slug}`}
      className="comp-card group relative flex flex-col overflow-hidden fade-up cursor-pointer"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-200" data-testid={`card-image-wrap-${comp.slug}`}>
        <PrizeImage comp={comp} variant="card" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          <Badge variant="dark" testId={`card-category-${comp.slug}`}>
            <Tag size={10} weight="fill" /> {categoryLabel(comp.category)}
          </Badge>
          {isClosingSoon && (
            <Badge variant="warn" icon={Clock}>Closing soon</Badge>
          )}
          {isBestValue && <Badge variant="gold" icon={Sparkle}>Best value</Badge>}
          {isDemoListing && (
            <Badge variant="neutral" testId={`badge-demo-${comp.slug}`}>
              Demo listing
            </Badge>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <div
            className="h-12 w-12 rounded-full flex items-center justify-center text-sm font-bold font-mono-num bg-[var(--cc-navy)] text-white border-2"
            style={{ borderColor: scoreColor(overall) }}
            data-testid={`card-overall-${comp.slug}`}
          >
            {overall}
          </div>
        </div>
      </div>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div>
          {operatorName && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              <Badge variant="neutral" testId={`card-operator-${comp.slug}`}>
                {operatorName}
              </Badge>
              {isBotbSkill && (
                <Badge variant="dark" testId={`badge-botb-skill-${comp.slug}`}>
                  Skill-based
                </Badge>
              )}
              {isMckinneyAwarenessOnly && (
                <Badge variant="warn" testId={`badge-mckinney-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isLlfAwarenessOnly && (
                <Badge variant="warn" testId={`badge-llf-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isKiltedAwarenessOnly && (
                <Badge variant="warn" testId={`badge-kilted-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isUkccAwarenessOnly && (
                <Badge variant="warn" testId={`badge-ukcc-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isBountyAwarenessOnly && (
                <Badge variant="warn" testId={`badge-bounty-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isStormAwarenessOnly && (
                <Badge variant="warn" testId={`badge-storm-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isRedlineAwarenessOnly && (
                <Badge variant="warn" testId={`badge-redline-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isPrizeParadiseAwarenessOnly && (
                <Badge variant="warn" testId={`badge-prizeparadise-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isCaddyAwarenessOnly && (
                <Badge variant="warn" testId={`badge-caddy-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isHotCompsAwarenessOnly && (
                <Badge variant="warn" testId={`badge-hotcomps-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isApexAwarenessOnly && (
                <Badge variant="warn" testId={`badge-apex-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isJammyAwarenessOnly && (
                <Badge variant="warn" testId={`badge-jammy-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isPawsomeAwarenessOnly && (
                <Badge variant="warn" testId={`badge-pawsome-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isLincolnshireAwarenessOnly && (
                <Badge variant="warn" testId={`badge-lincolnshire-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isDriftAwarenessOnly && (
                <Badge variant="warn" testId={`badge-drift-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isPalladiumAwarenessOnly && (
                <Badge variant="warn" testId={`badge-palladium-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isPristineAwarenessOnly && (
                <Badge variant="warn" testId={`badge-pristine-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isThatPrizeGuyAwarenessOnly && (
                <Badge variant="warn" testId={`badge-thatprizeguy-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isBrightAwarenessOnly && (
                <Badge variant="warn" testId={`badge-bright-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isSalexmoAwarenessOnly && (
                <Badge variant="warn" testId={`badge-salexmo-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
              {isClickAwarenessOnly && (
                <Badge variant="warn" testId={`badge-click-awareness-${comp.slug}`}>
                  Awareness only
                </Badge>
              )}
            </div>
          )}
          <h3 className="font-display text-lg font-semibold leading-tight text-[var(--cc-text-dark)] line-clamp-2">
            <Link
              to={detailPath}
              onClick={handleDetailLinkNavigate}
              aria-label={`View breakdown for ${comp.title}`}
              className="focus:outline-none focus:ring-2 focus:ring-[var(--cc-gold)]"
            >
              {comp.title}
            </Link>
          </h3>
        </div>

        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200">
          <div>
            <div className="overline text-[var(--cc-text-dark-muted)]">Ticket</div>
            <div className="font-mono-num text-sm font-semibold text-[var(--cc-text-dark)]">
              {formatGBPDecimal(displayTicketPrice)}
            </div>
          </div>
          <div>
            <div className="overline text-[var(--cc-text-dark-muted)]">Odds</div>
            <div
              className={`font-mono-num text-sm font-semibold ${cardOddsReady ? "text-[var(--cc-text-dark)]" : "text-amber-700"}`}
              title={cardOddsReady ? undefined : oddsUnavailableReason}
              aria-label={oddsState.ariaLabel}
              data-testid={`odds-cell-${comp.slug}`}
            >
              {oddsState.displayText}
            </div>
          </div>
          <div data-testid={`card-prize-${comp.slug}`}>
            <div className="overline text-[var(--cc-text-dark-muted)]">{publicPrizeCell(comp).label}</div>
            <div className="font-mono-num text-sm font-semibold text-[var(--cc-text-dark)]">
              {publicPrizeCell(comp).value}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 pt-1">
          {isTransparent && (
            <Badge variant="good" icon={CheckCircle} testId={`badge-transparent-${comp.slug}`}>Transparent</Badge>
          )}
          {!isTransparent && dataQuality === "partial_data" && (
            <Badge variant="warn" icon={Info} testId={`badge-partial-${comp.slug}`}>Partial data</Badge>
          )}
          {!isTransparent && dataQuality === "check_terms" && (
            <Badge variant="warn" icon={Info} testId={`badge-check-terms-${comp.slug}`}>Check terms</Badge>
          )}
          {comp.free_entry_competition && <Badge variant="neutral">Free competition</Badge>}
          {hasFreeOrPostalEntryMethod(comp) && !comp.free_entry_competition && (
            <Badge variant="neutral">Postal entry route</Badge>
          )}
          {hasRisk && <Badge variant="bad" icon={Warning}>Risk flag</Badge>}
          {isBotb && !hasTrustedTotalCap && (
            <Badge variant="warn" icon={Info} testId={`badge-botb-no-odds-${comp.slug}`}>
              {isBotbSkill ? "No standard odds available" : oddsUnavailableReason}
            </Badge>
          )}
          {((!cardOddsReady && !isAwarenessOnly) || displayTicketPrice == null) && (
            <Badge variant="warn" icon={Info} testId={`badge-data-incomplete-${comp.slug}`}>Data incomplete</Badge>
          )}
        </div>

        <div className="pt-2 mt-auto flex items-center justify-between text-xs text-[var(--cc-text-dark-muted)] border-t border-slate-100">
          <span className="font-mono-num">CompCheck {s.score_band || ""}</span>
          {isExternalCta ? (
            <span className="inline-flex flex-col items-end gap-1">
              <a
                href={ctaDestinationUrl}
                target="_blank"
                rel={affiliateDisclosure.isAffiliateLink ? "nofollow sponsored noreferrer" : "noreferrer"}
                onClick={(event) => event.stopPropagation()}
                data-testid={`card-cta-label-${comp.slug}`}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-sm bg-black px-3 py-2.5 text-center font-display text-sm font-semibold leading-snug text-[var(--cc-gold)] focus:outline-none focus:ring-2 focus:ring-[var(--cc-gold)] focus:ring-offset-2"
              >
                Visit operator site <ArrowRight size={14} weight="bold" />
              </a>
              <AffiliateLinkLabel
                record={comp}
                destinationUrl={ctaDestinationUrl}
                testId={`card-affiliate-label-${comp.slug}`}
              />
            </span>
          ) : (
            <Link
              to={detailPath}
              onClick={handleDetailLinkNavigate}
              data-testid={`card-cta-label-${comp.slug}`}
              className="inline-flex items-center gap-1 text-[var(--cc-bronze)] font-semibold group-hover:gap-1.5 transition-all"
            >
              {/* Honest CTA copy honouring source_link_type. */}
              {ctaLabel === "View competition" ? "View breakdown" : ctaLabel} <ArrowRight size={12} weight="bold" />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
